import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink, Loader2, Plus, Upload, X } from 'lucide-react';
import { BulkUploadModal } from '../components/BulkUploadModal';
import { companyApi } from '../services/companyApi';
import { dealApi } from '../services/dealApi';
import { cashflowApi } from '../services/cashflowApi';
import { vehicleApi } from '../services/vehicleApi';
import { COMPANY_STATUS_LABEL, COMPANY_STATUS_STYLE, Company, CompanyForm } from '../types/company';
import { Deal } from '../types/deal';
import { Cashflow } from '../types/cashflow';
import { Vehicle, shortWon } from '../types/vehicle';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { SidePanel } from '../components/SidePanel';
import { CompanyDetail } from './CompanyDetailPage';

/**
 * 투자처 — 우리가 돈을 넣은 회사.
 *
 * ⚠️ 한 회사에 자금원이 둘이면 **투자 건도 둘**이다. 원장은 그래야 맞고,
 *    여기서는 **회사 기준으로 합쳐** 보여준다.
 */
export const CompanyListPage: React.FC = () => {
  const [items, setItems] = useState<Company[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  // 줄을 누르면 오른쪽 패널로 상세(투자 건 포함)를 연다 — UI 규칙
  const [sel, setSel] = useState<number | null>(null);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d, f, v] = await Promise.all([
        companyApi.list(), dealApi.list(), cashflowApi.list(), vehicleApi.list(),
      ]);
      setItems(c); setDeals(d); setFlows(f); setVehicles(v);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vName = (id: number) => vehicles.find((v) => v.id === id)?.name ?? '—';

  /** 회사별로 건을 합친다 — 자금원이 여럿이면 여기서 모인다 */
  const rows = useMemo(() => items.map((c) => {
    const ds = deals.filter((d) => d.companyId === c.id);
    const ids = new Set(ds.map((d) => d.id));
    const invested = flows
      .filter((f) => f.kind === 'investment' && f.dealId !== null && ids.has(f.dealId))
      .reduce((s, f) => s + f.amount, 0);
    const returned = flows
      .filter((f) => (f.kind === 'exit' || f.kind === 'dividend') && f.dealId !== null && ids.has(f.dealId))
      .reduce((s, f) => s + f.amount, 0);
    return { c, ds, invested, returned };
  }), [items, deals, flows]);

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'intro',
      target: '[data-tour="title"]',
      title: '투자처',
      content: '우리가 돈을 넣었거나 검토 중인 회사입니다. 회사 정보만 적고, 얼마를 넣었는지는 투자 건과 원장에서 적습니다.',
    },
    {
      name: 'add',
      target: '[data-tour="add"]',
      title: '투자처 추가',
      content: '회사명만 있으면 등록됩니다. 업종·사업자번호·홈페이지는 알 때 채웁니다.',
      placement: 'left',
    },
    {
      name: 'vehicles',
      target: '[data-tour="vehicles"]',
      title: '투자 자금원',
      content: '한 회사에 고유자금과 위탁자금이 같이 들어가면 투자 건이 둘로 잡힙니다. 이 화면은 그 건들을 회사 기준으로 합쳐 보여줍니다.',
    },
    {
      name: 'sums',
      target: '[data-tour="sums"]',
      title: '투자 · 회수',
      content: '이 회사에 걸린 모든 건의 원장을 더한 값입니다. 회수에는 매각 대금과 배당이 들어갑니다. 회사 이름을 누르면 자금원별 건과 평가 이력을 봅니다.',
    },
  ], []);

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div data-tour="title">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            투자처
            <TourButton onClick={() => setTourRun(true)} />
          </h1>
        </div>
        <div className="flex gap-2 flex-shrink-0">
        <button onClick={() => setBulk(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
          <Upload size={13} /> 일괄 등록
        </button>
        <button data-tour="add" onClick={() => setOpen(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex-shrink-0">
          <Plus size={13} /> 투자처 추가
        </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" /> 불러오는 중...
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
          <p className="text-sm text-slate-500">투자처가 아직 없습니다.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5">회사</th>
                <th className="text-left font-medium px-2 py-2.5">업종</th>
                <th data-tour="vehicles" className="text-left font-medium px-2 py-2.5">투자 자금원</th>
                <th data-tour="sums" className="text-right font-medium px-2 py-2.5">투자</th>
                <th className="text-right font-medium px-2 py-2.5">회수</th>
                <th className="text-left font-medium px-2 py-2.5">상태</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, ds, invested, returned }) => (
                <tr key={c.id} onClick={() => setSel(c.id)}
                  className={`border-b border-slate-100 last:border-b-0 align-top cursor-pointer hover:bg-slate-50 ${sel === c.id ? 'bg-indigo-50/60' : ''}`}>
                  <td className="px-4 py-3">
                    <button type="button" className="flex items-center gap-1.5 font-medium text-slate-900 hover:text-indigo-600 text-left">
                      <Building2 size={13} className="text-slate-300 flex-shrink-0" />
                      {c.name}
                    </button>
                    {c.homepage && (
                      <a href={c.homepage} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                        홈페이지 <ExternalLink size={10} />
                      </a>
                    )}
                  </td>
                  <td className="px-2 py-3 text-xs text-slate-600">{c.sector ?? '—'}</td>
                  <td className="px-2 py-3 text-xs text-slate-600">
                    {ds.length === 0 ? <span className="text-slate-400">아직 없음</span> : (
                      <div className="space-y-0.5">
                        {ds.map((d) => <div key={d.id}>{vName(d.vehicleId)}</div>)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-3 text-right font-medium text-slate-900 whitespace-nowrap tabular-nums">
                    {shortWon(invested)}
                  </td>
                  <td className="px-2 py-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                    {returned > 0 ? shortWon(returned) : '—'}
                  </td>
                  <td className="px-2 py-3 whitespace-nowrap">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${COMPANY_STATUS_STYLE[c.status]}`}>
                      {COMPANY_STATUS_LABEL[c.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BulkUploadModal open={bulk} existing={items.map((c) => c.name)} onClose={() => setBulk(false)} onDone={() => { setBulk(false); load(); }} />
      <CompanyFormModal open={open} onClose={() => setOpen(false)}
        onSaved={() => { setOpen(false); load(); }} />
      {sel !== null && (
        <SidePanel onClose={() => { setSel(null); load(); }}
          header={<div className="flex items-center gap-2 text-xs text-slate-500">투자처 상세
            <Link to={`/companies/${sel}`} className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline">전체 화면 <ExternalLink size={11} /></Link></div>}>
          <CompanyDetail key={sel} companyId={sel} inPanel />
        </SidePanel>
      )}

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const EMPTY: CompanyForm = { name: '', bizNo: '', sector: '', foundedOn: '', homepage: '', note: '' };

const CompanyFormModal: React.FC<{ open: boolean; onClose: () => void; onSaved: () => void }> = ({
  open, onClose, onSaved,
}) => {
  const [form, setForm] = useState<CompanyForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);
  if (!open) return null;

  const set = <K extends keyof CompanyForm>(k: K, v: CompanyForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-500 mb-1';

  const save = async () => {
    setSaving(true); setErr(null);
    try { await companyApi.create(form); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">투자처 추가</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className={label} htmlFor="c-name">회사명</label>
            <input id="c-name" className={input} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="c-sector">업종</label>
              <input id="c-sector" className={input} value={form.sector} onChange={(e) => set('sector', e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="c-founded">설립일</label>
              <input id="c-founded" type="date" className={input} value={form.foundedOn}
                onChange={(e) => set('foundedOn', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="c-biz">사업자번호</label>
              <input id="c-biz" className={input} value={form.bizNo} onChange={(e) => set('bizNo', e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="c-home">홈페이지</label>
              <input id="c-home" className={input} value={form.homepage}
                onChange={(e) => set('homepage', e.target.value)} placeholder="https://" />
            </div>
          </div>
          <div>
            <label className={label} htmlFor="c-note">메모</label>
            <textarea id="c-note" className={input + ' h-16 resize-none'} value={form.note}
              onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={!form.name.trim() || saving}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '등록'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
